import React, { FC, useCallback, useEffect } from 'react'
import { makeStyles } from '@mui/styles'
import { Grid } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { FormProvider, useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'
import { endOfDay, startOfDay } from 'date-fns'
import { useDispatch, useSelector } from 'react-redux'

import { AuditLogsItemDto, AuditLogsItemRequestDto } from '@sparling/financial-common'
import { columns } from './logsList'
import { DataPicker } from '../../../components/DatePicker'
import { Search } from '../../../components/Search'
import { IDataItem, TableComponent } from '../../../components/Table'
import { FilterSelect } from '../../../components/FilterSelect'
import { Loader } from '../../../components/Loader'
import { ICompanyData } from '../CompanyProfileTabs/interfaces'
import { auditLogsLoading, getAuditLogsData, getLogTypesData, logTypesLoading } from '../../../store/selectors'
import { getAuditLogsRequest, getLogTypesRequest, resetAuditLogsFilters } from '../../../store/actions'

const useStyles = makeStyles((theme: Theme) => ({
  topWrap: {
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    paddingTop: theme.spacing(4),
  },
  rightSide: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    width: 'max-content',
  },
  title: {
    lineHeight: 0,
  },
  button: {
    paddingLeft: 15,
  },
  table: {
    paddingTop: theme.spacing(1),
  },
}))

interface CompanyAuditProps {
  data?: ICompanyData
}

interface IFiltersFields {
  startDate?: string
  endDate?: string
  type?: string
  search?: string
}

export const CompanyAuditLogs: FC<CompanyAuditProps> = ({ data }) => {
  const companyId = data?.idSf
  const classes = useStyles()
  const history = useHistory()
  const dispatch = useDispatch()
  const auditLogsIsLoading = useSelector(auditLogsLoading)
  const { meta, items } = useSelector(getAuditLogsData)
  const logTypesIsLoading = useSelector(logTypesLoading)
  const logTypes = useSelector(getLogTypesData)
  const { tab } = useParams<{ tab: string }>()

  const formMethods = useForm<IFiltersFields>({
    mode: 'onBlur',
  })
  const { watch, getValues } = formMethods

  useEffect(() => {
    dispatch(resetAuditLogsFilters())
  }, [dispatch])

  const loadAuditLogsData = useCallback(
    (page = 1) => {
      if (companyId)
        dispatch(
          getAuditLogsRequest({
            page,
            id: companyId,
            client: 'company',
          }),
        )
      history.push(`${tab}?page=${page}`)
    },
    [companyId, dispatch, history, tab],
  )

  const loadLogTypesData = useCallback(() => {
    if (companyId) dispatch(getLogTypesRequest({ id: companyId, client: 'company' }))
  }, [dispatch, companyId])

  useEffect(() => {
    loadAuditLogsData()
    loadLogTypesData()
  }, [loadAuditLogsData, loadLogTypesData])

  useEffect(() => {
    const subscription = watch(() => {
      if (companyId)
        dispatch(getAuditLogsRequest({ id: companyId, client: 'company', ...(getValues() as AuditLogsItemRequestDto) }))
    })

    return () => subscription.unsubscribe()
  }, [dispatch, companyId, getValues, watch])

  return (
    <FormProvider {...formMethods}>
      <Grid container className={classes.topWrap}>
        <Grid container className={classes.rightSide}>
          <DataPicker
            placeholder={'Start Date'}
            margin="8px 8px 8px 0"
            dataPickerName={'startDate'}
            required={false}
            maxDate={getValues().endDate}
            formatValue={startOfDay}
          />
          <DataPicker
            placeholder={'End Date'}
            margin={8}
            dataPickerName={'endDate'}
            required={false}
            minDate={getValues().startDate}
            formatValue={endOfDay}
          />
          <FilterSelect
            inputName={'type'}
            placeholder="Type"
            width={138}
            ml={8}
            mr={8}
            enums={logTypes}
            disable={logTypesIsLoading}
          />
        </Grid>
        <Search inputName={'search'} pr={0} pl={0} />
      </Grid>
      {auditLogsIsLoading ? (
        <Loader />
      ) : (
        <Grid container className={classes.table}>
          <TableComponent
            columns={columns}
            data={items as IDataItem<AuditLogsItemDto>[]}
            params={meta}
            loadData={loadAuditLogsData}
          />
        </Grid>
      )}
    </FormProvider>
  )
}
